import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "dialog-hero" }
const _hoisted_2 = { class: "hero-icon-wrap" }
const _hoisted_3 = { class: "endpoint-info" }
const _hoisted_4 = { class: "info-chip" }
const _hoisted_5 = { class: "info-chip" }
const _hoisted_6 = { class: "dialog-body" }
const _hoisted_7 = { class: "table-head" }
const _hoisted_8 = { class: "device-table" }
const _hoisted_9 = { class: "m-cell cell-check" }
const _hoisted_10 = { class: "m-cell cell-name" }
const _hoisted_11 = { class: "m-cell cell-en" }
const _hoisted_12 = { class: "m-cell cell-file" }
const _hoisted_13 = {
  key: 0,
  class: "file-text"
}
const _hoisted_14 = {
  key: 1,
  class: "file-missing"
}
const _hoisted_15 = { class: "m-cell cell-status" }
const _hoisted_16 = {
  key: 0,
  class: "st-pending"
}
const _hoisted_17 = {
  key: 1,
  class: "st-ok"
}
const _hoisted_18 = ["title"]
const _hoisted_19 = {
  key: 1,
  class: "all-done"
}
const _hoisted_20 = {
  key: 2,
  class: "ready-box"
}
const _hoisted_21 = { class: "ready-label" }

import { ref, computed, watch } from "vue";
import { ElMessage } from "element-plus";
import { Box, Promotion, CircleCheckFilled } from "@element-plus/icons-vue";
import { migrateDeviceProtocolsApi } from "@/api/cert/certBatchApi";
import { listProtocolFilesApi } from "@/api/svc005Api";
import { loadTranslations, translateName } from "@/composables/useTranslation";

/** 待迁移行（对账缺失设备 + 勾选态 + 本轮迁移结果） */
interface MigrateRow {
  enName: string;
  devName: string;
  protName: string | null;
  /** null=待迁移（未参与本轮下发）；true/false=本轮 [5,6] 对账结果 */
  success: boolean | null;
  errorMsg: string | null;
  checked: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'MigrateDeviceProtocolsDialog',
  props: {
    modelValue: { type: Boolean },
    endpointId: {},
    deviceId: {},
    shipId: {},
    shipName: {}
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const visible = ref(props.modelValue);
const loading = ref(false);
const migrating = ref(false);
const rows = ref<MigrateRow[]>([]);
/** 已就位文件（enName 为准；中文名展示走全局翻译缓存 translateName，不依赖缓存表 dev_name 列） */
const readyFiles = ref<{ enName: string }[]>([]);
/** 无终端文件记录（缓存为空：通常为换硬件后的新终端） */
const noRecord = ref(false);
const lastRoundSummary = ref<{ type: string; text: string } | null>(null);

const checkableCount = computed(
  () => rows.value.filter((r) => r.protName && r.success !== true).length
);
const checkedCount = computed(
  () =>
    rows.value.filter((r) => r.checked && r.protName && r.success !== true)
      .length
);
const allChecked = computed(
  () =>
    checkableCount.value > 0 &&
    rows.value.every((r) => !r.protName || r.success === true || r.checked)
);

watch(
  () => props.modelValue,
  (val) => {
    visible.value = val;
    if (val) {
      lastRoundSummary.value = null;
      loadList();
    }
  }
);
watch(visible, (val) => emit("update:modelValue", val));

/**
 * 读终端文件记录与船舶设备清单比对（零 MQTT 交互，秒开）。
 *
 * <p>数据源：svc_terminal_protocol_file 缓存表——用户端每次添加/修改/删除设备、
 * 迁移设备、[5,7] 同步后都会以终端返回列表全量刷新，平时即代表终端真实文件状态；
 * 换硬件时后端会清空该缓存（旧板子记录失真），此时无记录 = 新终端必然全缺。</p>
 */
const loadList = async () => {
  loading.value = true;
  rows.value = [];
  readyFiles.value = [];
  try {
    // 确保全局翻译缓存可用（App 启动已加载则幂等返回，展示设备中文名用）
    loadTranslations();
    const result = await listProtocolFilesApi(props.shipId);
    if (!result) {
      ElMessage.error("读取比对结果失败");
      return;
    }
    rows.value = (result.missingDevices || []).map((d) => ({
      enName: d.enName,
      devName: d.devName,
      protName: d.protName,
      success: null,
      errorMsg: null,
      checked: true, // 默认全勾（无可迁文件的行勾选被禁用）
    }));
    readyFiles.value = (result.files || [])
      .filter((f) => !!f.enName)
      .map((f) => ({ enName: f.enName as string }));
    // 无任何终端文件记录 = 换过硬件的新终端（正常添加过设备的船必有记录）
    noRecord.value = (result.files || []).length === 0;
  } catch (e: any) {
    ElMessage.error(e?.message || "读取比对结果失败");
  } finally {
    loading.value = false;
  }
};

/** 已就位设备显示名：全局翻译缓存（svc_device_name_translation）英文转中文，未命中显示英文 */
const displayName = (f: { enName: string }) => translateName(f.enName);

const toggleCheckAll = (val: boolean | string | number) => {
  rows.value.forEach((r) => {
    if (r.protName && r.success !== true) {
      r.checked = val === true;
    }
  });
};

/** 批量迁移勾选设备（[5,5] 下发，[5,6] 返回列表逐台对账，幂等可无限重试） */
const migrate = async () => {
  const targets = rows.value
    .filter((r) => r.checked && r.protName)
    .map((r) => r.enName);
  if (targets.length === 0) {
    ElMessage.warning("勾选的设备均无协议文件记录，无法迁移");
    return;
  }
  migrating.value = true;
  try {
    const result = await migrateDeviceProtocolsApi(props.endpointId, targets);
    lastRoundSummary.value = null;
    if (result.reason) {
      ElMessage.error(`迁移未完成：${result.reason}`);
      lastRoundSummary.value = {
        type: "error",
        text: `迁移未完成：${result.reason}，可重试`,
      };
    } else {
      const ok = result.results.filter((x) => x.success).length;
      const fail = result.total - ok;
      if (fail === 0) {
        ElMessage.success(`迁移成功：${ok} 台设备协议已就位`);
        lastRoundSummary.value = {
          type: "success",
          text: `迁移成功 ${ok} 台，全部就位`,
        };
      } else {
        ElMessage.warning(`本轮成功 ${ok} 台、失败 ${fail} 台，可继续重试`);
        lastRoundSummary.value = {
          type: "warning",
          text: `本轮成功 ${ok} 台、失败 ${fail} 台（失败设备已保持勾选，可直接重试）`,
        };
      }
    }
    // 重载比对结果：后端已用 [5,6] 列表刷缓存，成功者从待迁移消失、
    // 失败者保留——杜绝"已就位的行仍留在待迁移列表、可再次勾选下发"的问题
    await loadList();
    // 上轮失败结果合并回行（展示失败原因并保持勾选便于重试；成功行已不在列表）
    for (const r of rows.value) {
      const hit = result.results.find(
        (x) => x.enName === r.enName && !x.success
      );
      if (hit) {
        r.success = false;
        r.errorMsg = hit.errorMsg;
        r.checked = true;
      }
    }
  } catch (e: any) {
    ElMessage.error(e?.message || "迁移失败");
  } finally {
    migrating.value = false;
  }
};

const handleClose = () => {
  lastRoundSummary.value = null;
  noRecord.value = false;
};

return (_ctx: any,_cache: any) => {
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_alert = _resolveComponent("el-alert")!
  const _component_el_checkbox = _resolveComponent("el-checkbox")!
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_dialog = _resolveComponent("el-dialog")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createBlock(_component_el_dialog, {
    modelValue: visible.value,
    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((visible).value = $event)),
    title: "迁移设备",
    width: "680px",
    "close-on-click-modal": false,
    class: "migrate-dialog",
    onClose: handleClose
  }, {
    footer: _withCtx(() => [
      _createVNode(_component_el_button, {
        size: "large",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (visible.value = false))
      }, {
        default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
          _createTextVNode("关闭", -1)
        ]))]),
        _: 1
      }),
      _createVNode(_component_el_button, {
        type: "primary",
        size: "large",
        class: "migrate-btn",
        loading: migrating.value,
        disabled: checkedCount.value === 0 || loading.value,
        onClick: migrate
      }, {
        default: _withCtx(() => [
          _createVNode(_component_el_icon, null, {
            default: _withCtx(() => [
              _createVNode(_unref(Promotion))
            ]),
            _: 1
          }),
          _createElementVNode("span", null, "迁移选中设备（" + _toDisplayString(checkedCount.value) + "）", 1)
        ]),
        _: 1
      }, 8, ["loading", "disabled"])
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_el_icon, {
            size: 22,
            color: "#fff"
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(Box))
            ]),
            _: 1
          })
        ]),
        _cache[2] || (_cache[2] = _createElementVNode("div", { class: "hero-text" }, [
          _createElementVNode("h3", null, "迁移设备协议 → 新终端"),
          _createElementVNode("p", null, " 更换硬件后新板子本地没有设备协议文件，此处将缺失的协议文件重新下发。 依据终端文件记录与船舶设备清单自动比对；运营协议出厂已预置，无需迁移。 ")
        ], -1))
      ]),
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("span", _hoisted_4, "终端 " + _toDisplayString(__props.deviceId || "—"), 1),
        _createElementVNode("span", _hoisted_5, "已绑船舶 " + _toDisplayString(__props.shipName || "—"), 1),
        _createElementVNode("span", {
          class: _normalizeClass(["info-chip", rows.value.length > 0 ? 'chip-warn' : 'chip-ok'])
        }, _toDisplayString(loading.value
            ? "比对中…"
            : rows.value.length > 0
            ? `待迁移 ${rows.value.length} 台`
            : "无需迁移"), 3)
      ]),
      (noRecord.value && !loading.value && rows.value.length > 0)
        ? (_openBlock(), _createBlock(_component_el_alert, {
            key: 0,
            type: "info",
            closable: false,
            class: "no-record-alert",
            title: "暂无终端文件记录（通常为更换硬件后的新终端），已列出全部设备待迁移；重复迁移无副作用（覆盖导入）"
          }))
        : _createCommentVNode("", true),
      (lastRoundSummary.value)
        ? (_openBlock(), _createBlock(_component_el_alert, {
            key: 1,
            type: lastRoundSummary.value.type,
            closable: false,
            class: "round-summary-alert",
            title: lastRoundSummary.value.text
          }, null, 8, ["type", "title"]))
        : _createCommentVNode("", true),
      _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_6, [
        (rows.value.length > 0)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              _createElementVNode("div", _hoisted_7, [
                _createVNode(_component_el_checkbox, {
                  "model-value": allChecked.value,
                  indeterminate: checkedCount.value > 0 && checkedCount.value < checkableCount.value,
                  onChange: toggleCheckAll
                }, {
                  default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                    _createTextVNode(" 待迁移设备 ", -1)
                  ]))]),
                  _: 1
                }, 8, ["model-value", "indeterminate"]),
                _cache[4] || (_cache[4] = _createElementVNode("span", { class: "head-tip" }, " 默认全选；迁移后失败的设备保持勾选，可一键重试 ", -1))
              ]),
              _createElementVNode("div", _hoisted_8, [
                _cache[5] || (_cache[5] = _createElementVNode("div", { class: "m-row m-row-head" }, [
                  _createElementVNode("div", { class: "m-cell cell-check" }),
                  _createElementVNode("div", { class: "m-cell cell-name" }, "设备名称"),
                  _createElementVNode("div", { class: "m-cell cell-en" }, "英文键名"),
                  _createElementVNode("div", { class: "m-cell cell-file" }, "协议文件"),
                  _createElementVNode("div", { class: "m-cell cell-status" }, "状态")
                ], -1)),
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(rows.value, (row) => {
                  return (_openBlock(), _createElementBlock("div", {
                    key: row.enName,
                    class: "m-row"
                  }, [
                    _createElementVNode("div", _hoisted_9, [
                      _createVNode(_component_el_checkbox, {
                        modelValue: row.checked,
                        "onUpdate:modelValue": ($event: any) => ((row.checked) = $event),
                        disabled: !row.protName || row.success === true
                      }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled"])
                    ]),
                    _createElementVNode("div", _hoisted_10, _toDisplayString(row.devName || "—"), 1),
                    _createElementVNode("div", _hoisted_11, _toDisplayString(row.enName || "—"), 1),
                    _createElementVNode("div", _hoisted_12, [
                      (row.protName)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_13, _toDisplayString(row.protName), 1))
                        : (_openBlock(), _createElementBlock("span", _hoisted_14, "无协议文件记录"))
                    ]),
                    _createElementVNode("div", _hoisted_15, [
                      (row.success === null)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_16, "待迁移"))
                        : (row.success)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_17, "✓ 已就位"))
                          : (_openBlock(), _createElementBlock("span", {
                              key: 2,
                              class: "st-fail",
                              title: row.errorMsg || ''
                            }, " ✕ " + _toDisplayString(row.errorMsg || "失败"), 9, _hoisted_18))
                    ])
                  ]))
                }), 128))
              ])
            ], 64))
          : (!loading.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                _createVNode(_component_el_icon, {
                  size: 40,
                  color: "#10b981"
                }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(CircleCheckFilled))
                  ]),
                  _: 1
                }),
                _cache[6] || (_cache[6] = _createElementVNode("p", null, "终端已持有全部设备协议文件，无需迁移", -1))
              ]))
            : _createCommentVNode("", true)
      ])), [
        [_directive_loading, loading.value]
      ]),
      (!loading.value && readyFiles.value.length > 0 && rows.value.length > 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
            _createElementVNode("span", _hoisted_21, "已就位 " + _toDisplayString(readyFiles.value.length) + " 台：", 1),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(readyFiles.value, (f) => {
              return (_openBlock(), _createBlock(_component_el_tag, {
                key: f.enName,
                size: "small",
                type: "success",
                effect: "plain",
                class: "ready-tag"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(displayName(f)), 1)
                ]),
                _: 2
              }, 1024))
            }), 128))
          ]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }, 8, ["modelValue"]))
}
}

})